import React, { useState } from "react";
import { Box, Stack, Typography, Chip } from "@mui/material";
import { Handshake } from "@phosphor-icons/react";
import { useParams } from "react-router-dom";
import { addYears, format } from "date-fns";
import { convertTokens } from "@date-fns/upgrade/v2";
import { useGlobalToast } from "components/GlobalToastProvider";
import { providerTypes } from "./providerTypes";
import useProvidersFormDialogMutations from "./useProvidersFormDialogMutations";
import ProviderListItemMenu from "./ProviderListItemMenu";

interface ProviderListItemProps {
  provider: any;
  onEdit: () => void;
  onCreateROI: () => void;
  onProviderChanges: () => void;
  documentRequests: any[];
  userPermissions: any;
  canEdit: boolean;
  editMode: boolean;
}

const ProviderListItem: React.FC<ProviderListItemProps> = ({
  provider,
  onEdit,
  onCreateROI,
  onProviderChanges,
  documentRequests,
  userPermissions,
  canEdit,
  editMode,
}) => {
  const [showRecentCollaboration, setShowRecentCollaboration] = useState(false);
  const { id: clientId } = useParams<{ id: string }>();
  const { showToast } = useGlobalToast();
  const { unlinkPhysicianMutation } = useProvidersFormDialogMutations();
  const { name, physician_type, id: physician_id, clinic_id } = provider;

  const mapProviderTypeToLabel = () => {
    const providerType = providerTypes.find((p: any) => p.value === physician_type);
    return providerType?.label || "";
  };

  const getCurrentROI = () => {
    const withDocument = documentRequests?.filter((req: any) => req.signature_url || req.document_id);

    let providerRequests = withDocument?.filter(
      (req: any) => String(req.provider_id) === String(physician_id)
    );

    if (!providerRequests?.length && clinic_id) {
      providerRequests = withDocument?.filter(
        (req: any) => String(req.clinic_id) === String(clinic_id)
      );
    }

    if (providerRequests && providerRequests.length > 0) {
      // Sort and return most recent
      const sorted = [...providerRequests].sort(
        (a: any, b: any) => new Date(b.created).getTime() - new Date(a.created).getTime()
      );
      return sorted[0];
    }
    return null;
  };

  const getRoiStatus = () => {
    const roi = getCurrentROI();
    if (!roi) return null;

    const expirationDate = new Date(addYears(new Date(roi.created), 1));
    const isPastDate = expirationDate < new Date();

    return {
      expired: isPastDate,
      date: expirationDate,
      roiId: roi.id,
      documentId: roi.document_id,
    };
  };

  const roiStatus = getRoiStatus();

  const handleRemoveProvider = async () => {
    try {
      await unlinkPhysicianMutation.mutateAsync({
        clientId,
        physician: { id: provider.id },
        clinic: { id: provider.clinic_id },
      });
      showToast({ message: "Provider Successfully removed" });
      onProviderChanges();
    } catch (error) {
      showToast({
        message: "Whoops! Something went wrong",
        errorState: true,
      });
      console.error("An error occurred:", error);
    }
  };

  const getSubtitle = () => {
    const providerTypeLabel = mapProviderTypeToLabel();
    if (roiStatus) {
      const expiredText = roiStatus.expired ? "ROI expired" : "Current ROI expires";
      const dateStr = format(roiStatus.date, convertTokens("MMMM D, YYYY"));
      return `${providerTypeLabel} | ${expiredText} ${dateStr}`;
    }
    return providerTypeLabel;
  };

  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: "16px",
        padding: "16px 24px",
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2}>
        {/* Provider Info */}
        <Stack gap="4px" flex={1}>
          <Typography
            sx={{
              fontSize: "18px",
              fontWeight: 500,
              lineHeight: 1.6,
              color: "text.primary",
            }}
          >
            {name}
          </Typography>
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: 400,
              lineHeight: 1.43,
              color: "text.secondary",
            }}
          >
            {getSubtitle()}
          </Typography>
        </Stack>

        {/* Actions */}
        <Stack direction="row" gap={1} alignItems="center">
          {/* Recent Collaboration Badge */}
          {showRecentCollaboration && (
            <Chip
              label="Recent Collaboration"
              icon={<Handshake weight="duotone" />}
              variant="outlined"
              size="small"
              color="info"
            />
          )}

          {/* Menu */}
          {canEdit && (
            <ProviderListItemMenu
              onEdit={onEdit}
              onCreateROI={onCreateROI}
              onRemove={handleRemoveProvider}
              userPermissions={userPermissions}
              editMode={editMode}
              providerId={String(physician_id)}
              clientId={clientId}
              onRecentCollaborationChange={setShowRecentCollaboration}
            />
          )}
        </Stack>
      </Stack>
    </Box>
  );
};

export default ProviderListItem;
